/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { absoluteSheetReference, invalidSimpleRowAddress, simpleRowAddress } from '../Cell';
import { ReferenceType } from './ColumnAddress';
export class RowAddress {
    constructor(sheet, row, type) {
        this.sheet = sheet;
        this.row = row;
        this.type = type;
    }
    static absolute(sheet, row) {
        return new RowAddress(sheet, row, ReferenceType.ABSOLUTE);
    }
    static relative(sheet, row) {
        return new RowAddress(sheet, row, ReferenceType.RELATIVE);
    }
    isRowAbsolute() {
        return (this.type === ReferenceType.ABSOLUTE);
    }
    isRowRelative() {
        return (this.type === ReferenceType.RELATIVE);
    }
    isAbsolute() {
        return (this.type === ReferenceType.ABSOLUTE && this.sheet !== null);
    }
    moved(toSheet, toRight, toBottom) {
        const newSheet = this.sheet === null ? null : toSheet;
        return new RowAddress(newSheet, this.row + toBottom, this.type);
    }
    shiftedByRows(numberOfColumns) {
        return new RowAddress(this.sheet, this.row + numberOfColumns, this.type);
    }
    toSimpleRowAddress(baseAddress) {
        const sheet = absoluteSheetReference(this, baseAddress);
        let row = this.row;
        if (this.isRowRelative()) {
            row = baseAddress.row + this.row;
        }
        return simpleRowAddress(sheet, row);
    }
    shiftRelativeDimensions(toRight, toBottom) {
        const row = this.isRowRelative() ? this.row + toBottom : this.row;
        return new RowAddress(this.sheet, row, this.type);
    }
    shiftAbsoluteDimensions(toRight, toBottom) {
        const row = this.isRowAbsolute() ? this.row + toBottom : this.row;
        return new RowAddress(this.sheet, row, this.type);
    }
    withAbsoluteSheet(sheet) {
        return new RowAddress(sheet, this.row, this.type);
    }
    isInvalid(baseAddress) {
        return this.toSimpleRowAddress(baseAddress).row < 0;
    }
    hash(withSheet) {
        const sheetPart = withSheet && this.sheet !== null ? `#${this.sheet}` : '';
        switch (this.type) {
            case ReferenceType.RELATIVE: {
                return `${sheetPart}#ROWR${this.row}`;
            }
            case ReferenceType.ABSOLUTE: {
                return `${sheetPart}#ROWA${this.row}`;
            }
        }
    }
    unparse(baseAddress) {
        const simpleAddress = this.toSimpleRowAddress(baseAddress);
        if (invalidSimpleRowAddress(simpleAddress)) {
            return undefined;
        }
        const dollar = this.type === ReferenceType.ABSOLUTE ? '$' : '';
        return `${dollar}${simpleAddress.row + 1}`;
    }
    exceedsSheetSizeLimits(maxRows) {
        return this.row >= maxRows;
    }
}
